import { useState } from 'react';

// CLIENT PORTAL — the ONE lockup, `Kyck ✕ <brand>` (§2, 2026-09-03).
//
// ⚠️⚠️ ONE COMPONENT, TWO CALL SITES — `PortalShell`'s `.psh-top` and the
// restyled report's `.rpt-top`. They each had their own `BrandLockup` before
// this, which is precisely how the portal came to have two looks in the first
// place: two implementations of "show the brand" drift the first time one of
// them is touched.
//
// ⚠️⚠️ THE KYCK MARK IS THE WHITE-TEXT SVG, AND IT IS ONLY EVER ON A DARK BAND.
// Composited and looked at before shipping (rule 12, not inferred): the wordmark
// is white with a green accent inside the `k`, so it reads on `ink-deep`
// (#081113) and `ink-soft` (#0e1b1e) and is very nearly INVISIBLE on cream.
// Both lockups live on bands that are dark in BOTH modes, which is what makes
// the white variant safe here — a Dark Text variant exists for anywhere else,
// and this component must not be moved to a light surface without it.
//
// ⚠️ THE FILE IS SERVED FROM `public/`, copied into the image by an explicit
// Dockerfile line. "The logo 404s in production" is a real failure mode with a
// real cause, so a failed load falls back to the WORD "Kyck" rather than a
// broken image.
//
// ⚠️ The source asset lives in `_architect/assets/`, which is GITIGNORED —
// deliberately, because it also holds licensed GT America and Aktiv Grotesk
// sources that must never enter the repo. So the SVG is COPIED into `public/`
// under a URL-safe name; it cannot be referenced where it sits.
export const KYCK_LOGO_URL = '/kyck-logo-white.svg';

/**
 * `Kyck ✕ <brand>`, or Kyck alone.
 *
 * ⚠️⚠️ A MULTI-BRAND GROUP SCOPE HAS `brandName: null` BY DESIGN — a group owner
 * running two marques gets kyck's own look rather than one brand's colours
 * painted over the other's numbers. So a null brand renders KYCK ALONE, with no
 * separator: `Kyck ✕ —` would read as a brand we failed to load.
 *
 * ⚠️ THE SEPARATOR IS AN ELEMENT, not the letter x. A literal `x` in a wordmark
 * position reads as part of a name; a muted `✕` reads as punctuation.
 */
export function PortalLockup({
  brandName,
  brandLogoUrl,
  brandLogoAlt,
  className,
}: {
  /** Null for no brand, or for a multi-brand scope. */
  brandName: string | null;
  brandLogoUrl?: string;
  brandLogoAlt?: string;
  /** The call site's own wrapper class — `psh-lockup` or `rpt-lockup-row`. */
  className?: string;
}) {
  const [kyckFailed, setKyckFailed] = useState(false);
  const [brandFailed, setBrandFailed] = useState(false);
  const showBrandLogo = !!brandLogoUrl && !brandFailed;
  const hasBrand = !!brandName || showBrandLogo;

  return (
    <div className={className ? `pl-lockup ${className}` : 'pl-lockup'}>
      {kyckFailed ? (
        // ⚠️ The WORD, not a broken image. Cream by class, never `--portal-ink`,
        // which defaults to slate-900 and would render near-black on the
        // ink-deep band — invisible, and findable only in a browser.
        <span className="pl-kyck-word">Kyck</span>
      ) : (
        <img
          className="pl-kyck"
          src={KYCK_LOGO_URL}
          alt="Kyck"
          onError={() => setKyckFailed(true)}
        />
      )}
      {hasBrand && (
        <>
          <span className="pl-x" aria-hidden="true">
            ✕
          </span>
          {showBrandLogo ? (
            <img
              className="pl-brand"
              src={brandLogoUrl}
              alt={brandLogoAlt ?? brandName ?? 'Brand'}
              // ⚠️ Falls back to the brand NAME, which is the NORMAL case: only
              // two brands have artwork at all.
              onError={() => setBrandFailed(true)}
            />
          ) : (
            <span className="pl-brand-word">{brandName}</span>
          )}
        </>
      )}
    </div>
  );
}
